import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const StripeCheckoutForm = ({ onError, onClose, returnUrl }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setErrorMsg('');

    // On success Stripe redirects to RETURN_URL — only reaches here on error
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    setProcessing(false);

    if (error) {
      setErrorMsg(error.message);
      onError?.(error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMsg && (
        <Typography variant="caption" color="error" sx={{ mt: 1.5, display: 'block' }}>
          {errorMsg}
        </Typography>
      )}
      <DialogActions sx={{ px: 0, pt: 2.5 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disableElevation
          disabled={!stripe || processing}
          startIcon={processing ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {processing ? 'Processing…' : 'Pay now'}
        </Button>
      </DialogActions>
    </form>
  );
};

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - clientId: string  — Stripe client_secret from the backend response
 * - paymentId: string — Stripe Payment Intent id (for tracking)
 * - onError: (error) => void
 * On success Stripe redirects to /charger; backend handles verification.
 */
export const StripePaymentModule = ({ open, onClose, clientId, paymentId, onError, returnUrl }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle1" fontWeight={500}>
          Complete payment
        </Typography>
      </Box>
      <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>

    <DialogContent sx={{ pt: 1 }}>
      {clientId ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: clientId,
            appearance: { theme: 'stripe' },
          }}
        >
          <StripeCheckoutForm onError={onError} onClose={onClose} returnUrl={returnUrl} />
        </Elements>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}
    </DialogContent>
  </Dialog>
);
